import React, { useEffect, useState } from 'react';
import { Mail, Send, Vote, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useSortable, SortTh, TableSearch } from '../components/SortableTable';
import { Input } from '../components/ui/input';
import { ImapSettingsCard } from '../components/ImapSettingsCard';
import { Textarea } from '../components/ui/textarea';

export default function InboxTab() {
  const { getInboxConfig, getForwardLog, simulateInbound, classLabel, simulateVoteReply, getVoteLog, productionReadiness, getReadinessSettings, updateReadinessSettings, sendReadinessReport, getReadinessHistory } = useAuth();
  const [inboxCfg, setInboxCfg] = useState(null);
  const [fwdLog, setFwdLog] = useState([]);
  const [simForm, setSimForm] = useState({ sender: '', to_address: 'tas+financial@rae.org.au', subject: '', body: '' });
  const [simBusy, setSimBusy] = useState(false);
  const [simResult, setSimResult] = useState(null);
  const [voteLog, setVoteLog] = useState([]);
  const [vForm, setVForm] = useState({ sender: '', token: '', body: 'Yes' });
  const [vBusy, setVBusy] = useState(false);
  const [vResult, setVResult] = useState(null);

  useEffect(() => {
    getInboxConfig().then(setInboxCfg).catch(() => {});
    getForwardLog().then(setFwdLog).catch(() => {});
    getVoteLog().then(setVoteLog).catch(() => {});
  }, [getInboxConfig, getForwardLog, getVoteLog]);

  const runSimulate = async () => {
    if (!simForm.sender.trim()) { window.alert('Enter a sender email'); return; }
    setSimBusy(true); setSimResult(null);
    try {
      const r = await simulateInbound(simForm);
      setSimResult(r);
      setFwdLog(await getForwardLog());
    } catch (e) { console.error(e); window.alert('Could not run the test.'); }
    setSimBusy(false);
  };

  const runVoteSimulate = async () => {
    if (!vForm.sender.trim() || !vForm.token.trim()) { window.alert('Enter the voter email and the vote token'); return; }
    setVBusy(true); setVResult(null);
    try {
      const r = await simulateVoteReply(vForm);
      setVResult(r);
      setVoteLog(await getVoteLog());
    } catch (e) { console.error(e); window.alert('Could not run the test.'); }
    setVBusy(false);
  };

  const VSTATUS = { recorded: '#eef6ee', rejected: '#fdecec', unclear: '#fff6e5', pending: '#f3f3f3' };

  const [qaResult, setQaResult] = useState(null);
  const [qaBusy, setQaBusy] = useState(false);
  const runQaCheck = async () => {
    setQaBusy(true); setQaResult(null);
    try { setQaResult(await productionReadiness()); }
    catch { window.alert('Could not run the check.'); }
    setQaBusy(false);
  };
  const CHECK_STYLE = { pass: { bg: '#eef6ee', fg: '#1c6b2c', mark: '✓' }, warn: { bg: '#fff6e5', fg: '#9a6b00', mark: '!' }, fail: { bg: '#fdecec', fg: '#8a1f11', mark: '✕' } };

  const [rptCfg, setRptCfg] = useState(null);
  const [rptRecipient, setRptRecipient] = useState('');
  const [rptBusy, setRptBusy] = useState(false);
  useEffect(() => { getReadinessSettings().then((c) => { setRptCfg(c); setRptRecipient(c.extra_recipient || ''); }).catch(() => {}); }, [getReadinessSettings]);
  const saveRpt = async (enabled) => {
    setRptBusy(true);
    try { const c = await updateReadinessSettings({ enabled, extra_recipient: rptRecipient }); setRptCfg(c); setRptRecipient(c.extra_recipient || ''); toast.success('Report settings saved.'); }
    catch { toast.error('Could not save.'); }
    setRptBusy(false);
  };
  const sendRptNow = async () => {
    setRptBusy(true);
    try { const r = await sendReadinessReport(); toast.success(r.sent ? `Report emailed to ${r.sent} of ${r.recipients} sysadmin(s).` : `No email delivered (recipients: ${r.recipients}) — check email settings.`); loadHistory(); }
    catch { toast.error('Could not send.'); }
    setRptBusy(false);
  };
  const [rptHistory, setRptHistory] = useState([]);
  const loadHistory = () => getReadinessHistory().then(setRptHistory).catch(() => {});
  useEffect(() => { loadHistory(); }, []); // eslint-disable-line

  const { sorted: sortedFwdLog, sort: fwdSort, toggle: toggleFwdSort, query: fwdQuery, setQuery: setFwdQuery } =
    useSortable(fwdLog, { searchFields: [(f) => f.sender, (f) => f.to_address, (f) => f.status] });

  return (
    <div data-testid="inbox-tab">
      <div className="bg-white rounded-lg shadow-sm border p-5 mb-4" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
        <h3 className="flex items-center gap-2 font-title text-lg mb-1" style={{ color: 'var(--navy)' }}>
          <Mail size={18} /> Plus-address forwarding
        </h3>
        <p className="text-sm mb-2" style={{ color: 'var(--olive)' }}>
          Emails sent to <strong>{inboxCfg ? inboxCfg.forward_address : 'tas@rae.org.au'}</strong> with a plus-address are forwarded to members by class.
          For example <code>tas+financial+committee@…</code> reaches everyone in either class. <code>+All</code> reaches every member and is restricted to Committee senders. Only recognised members may trigger a forward. <strong>Every forward is logged below.</strong>
        </p>
        <div className="flex flex-wrap gap-3 text-xs mt-2">
          <span className="px-2 py-1 rounded" style={{ background: inboxCfg && inboxCfg.imap_configured ? 'var(--sage)' : '#fde8e4', color: '#3a2c06' }}>
            Inbox monitoring: {inboxCfg && inboxCfg.imap_configured ? 'live' : inboxCfg && inboxCfg.imap && inboxCfg.imap.connected ? 'paused' : 'not connected — connect the mailbox below'}
          </span>
          <span className="px-2 py-1 rounded" style={{ background: inboxCfg && inboxCfg.email_sending_configured ? 'var(--sage)' : '#fde8e4', color: '#3a2c06' }}>
            Email sending: {inboxCfg && inboxCfg.email_sending_configured ? 'live' : 'mocked/logged'}
          </span>
        </div>
      </div>

      <ImapSettingsCard onChanged={() => getInboxConfig().then(setInboxCfg).catch(() => {})} />

      <div className="bg-white rounded-lg shadow-sm border p-5 mb-4" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
        <h4 className="font-title text-base mb-3" style={{ color: 'var(--navy)' }}>Test a forward</h4>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-sm block mb-1">Sender email (a member)</label>
            <Input data-testid="sim-sender" value={simForm.sender} onChange={(e) => setSimForm({ ...simForm, sender: e.target.value })} placeholder="member@example.com" />
          </div>
          <div>
            <label className="text-sm block mb-1">To (plus-address)</label>
            <Input data-testid="sim-to" value={simForm.to_address} onChange={(e) => setSimForm({ ...simForm, to_address: e.target.value })} placeholder="tas+financial@rae.org.au" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-sm block mb-1">Subject</label>
            <Input data-testid="sim-subject" value={simForm.subject} onChange={(e) => setSimForm({ ...simForm, subject: e.target.value })} />
          </div>
          <div className="sm:col-span-2">
            <label className="text-sm block mb-1">Message</label>
            <Textarea rows={3} data-testid="sim-body" value={simForm.body} onChange={(e) => setSimForm({ ...simForm, body: e.target.value })} />
          </div>
        </div>
        <button onClick={runSimulate} disabled={simBusy} data-testid="sim-run" className="mt-3 flex items-center gap-2 px-4 py-2 rounded text-sm font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)', opacity: simBusy ? 0.7 : 1 }}>
          <Send size={15} /> {simBusy ? 'Processing…' : 'Simulate incoming email'}
        </button>
        {simResult && (
          <div data-testid="sim-result" className="mt-3 rounded p-3 text-sm" style={{ background: simResult.status === 'forwarded' ? '#eef6ee' : '#fdecec', border: '1px solid rgba(0,0,0,0.1)' }}>
            <strong>{simResult.status.toUpperCase()}</strong> — {simResult.reason}
            {simResult.status === 'forwarded' && (
              <span> ({simResult.recipient_count} recipient(s); classes: {(simResult.matched_classes || []).map((c) => classLabel(c)).join(', ')}{simResult.mocked ? '; email mocked/logged' : ''})</span>
            )}
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm border p-5 mb-4" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid="qa-account-check">
        <h3 className="flex items-center gap-2 font-title text-lg mb-1" style={{ color: 'var(--navy)' }}>
          <ShieldCheck size={18} /> Production readiness
        </h3>
        <p className="text-sm mb-2" style={{ color: 'var(--olive)' }}>
          A quick go/no-go for the current environment: no QA test accounts, CORS locked to trusted origins, an encryption key set, and email secrets encrypted at rest. Run this on the live site before you rely on it.
        </p>
        <button onClick={runQaCheck} disabled={qaBusy} data-testid="qa-check-run" className="flex items-center gap-2 px-4 py-2 rounded text-sm font-title" style={{ background: 'var(--navy)', color: 'var(--cream)', opacity: qaBusy ? 0.7 : 1 }}>
          <ShieldCheck size={15} /> {qaBusy ? 'Checking…' : 'Run readiness check'}
        </button>
        {qaResult && (
          <div data-testid="qa-check-result" className="mt-3">
            <div className="rounded p-3 mb-2 font-title" style={{ background: qaResult.ready ? '#eef6ee' : '#fdecec', color: qaResult.ready ? '#1c6b2c' : '#8a1f11', border: '1px solid rgba(0,0,0,0.1)' }} data-testid="qa-check-verdict">
              {qaResult.ready ? 'GO — this environment looks production-ready.' : 'NO-GO — resolve the failing checks below before relying on this environment.'}
            </div>
            <ul className="space-y-1.5">
              {(qaResult.checks || []).map((c) => {
                const st = CHECK_STYLE[c.status] || CHECK_STYLE.warn;
                return (
                  <li key={c.id} className="flex items-start gap-2 rounded p-2 text-sm" style={{ background: st.bg }} data-testid={`qa-check-${c.id}`}>
                    <span className="font-title flex-shrink-0" style={{ color: st.fg }}>{st.mark}</span>
                    <span><span className="font-medium" style={{ color: 'var(--navy)' }}>{c.label}</span><span style={{ color: 'var(--olive)' }}> — {c.detail}</span></span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        <div className="mt-4 pt-3 border-t" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid="readiness-report-settings">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="font-title text-sm" style={{ color: 'var(--navy)' }}>Automatic readiness reports</div>
              <p className="text-xs" style={{ color: 'var(--olive)' }}>Email sysadmins this report after each deploy and once a day.{rptCfg?.last_sent ? ` Last sent ${new Date(rptCfg.last_sent).toLocaleString()}.` : ''}</p>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: 'var(--navy)' }}>
              <input type="checkbox" checked={!!rptCfg?.enabled} disabled={rptBusy || !rptCfg} onChange={(e) => saveRpt(e.target.checked)} data-testid="readiness-report-toggle" />
              {rptCfg?.enabled ? 'On' : 'Off'}
            </label>
          </div>
          <div className="flex items-end gap-2 mt-2 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs block mb-1" style={{ color: 'var(--olive)' }}>Extra recipient (optional)</label>
              <Input data-testid="readiness-report-recipient" value={rptRecipient} onChange={(e) => setRptRecipient(e.target.value)} onBlur={() => rptCfg && saveRpt(rptCfg.enabled)} placeholder="sysadmin@example.com" />
            </div>
            <button onClick={sendRptNow} disabled={rptBusy} data-testid="readiness-report-send" className="flex items-center gap-1.5 px-3 py-2 rounded text-sm font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)', opacity: rptBusy ? 0.7 : 1 }}>
              <Send size={14} /> Send now
            </button>
          </div>
          {rptCfg?.recipients?.length > 0 && <p className="text-xs mt-1.5" style={{ color: 'var(--olive)' }}>Recipients: {rptCfg.recipients.join(', ')}</p>}
          {rptHistory.length > 0 && (
            <div className="mt-3" data-testid="readiness-history">
              <div className="text-xs font-title mb-1" style={{ color: 'var(--navy)' }}>Recent reports</div>
              <ul className="space-y-0.5">
                {rptHistory.slice(0, 8).map((h, i) => (
                  <li key={i} className="text-xs flex items-center gap-2" style={{ color: 'var(--olive)' }}>
                    <span className="font-title" style={{ color: h.ready ? '#1c6b2c' : '#8a1f11' }}>{h.ready ? 'GO' : 'NO-GO'}</span>
                    <span>{new Date(h.at).toLocaleString()}</span>
                    <span>· {h.reason}</span>
                    {!h.ready && <span>· {h.fail_count} to fix</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border p-5 mb-4" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid="reply-vote-section">
        <h3 className="flex items-center gap-2 font-title text-lg mb-1" style={{ color: 'var(--navy)' }}>
          <Vote size={18} /> Reply-to-vote
        </h3>
        <p className="text-sm mb-2" style={{ color: 'var(--olive)' }}>
          Ballot emails now set a reply address of <strong>{inboxCfg ? inboxCfg.vote_address : 'vote@rae.org.au'}</strong>. When a member simply replies
          <em> Yes</em>, <em>No</em>, or <em>Abstain</em>, the monitor reads their choice and records it against the motion. The reply must come from the
          address the ballot was sent to. Use the tester below to simulate a reply (paste a token from a sent ballot).
        </p>
        <div className="grid sm:grid-cols-3 gap-3 mt-3">
          <div>
            <label className="text-sm block mb-1">Voter email (ballot recipient)</label>
            <Input data-testid="vote-sim-sender" value={vForm.sender} onChange={(e) => setVForm({ ...vForm, sender: e.target.value })} placeholder="member@example.com" />
          </div>
          <div>
            <label className="text-sm block mb-1">Vote token</label>
            <Input data-testid="vote-sim-token" value={vForm.token} onChange={(e) => setVForm({ ...vForm, token: e.target.value })} placeholder="paste token" />
          </div>
          <div>
            <label className="text-sm block mb-1">Reply text</label>
            <Input data-testid="vote-sim-body" value={vForm.body} onChange={(e) => setVForm({ ...vForm, body: e.target.value })} placeholder="Yes / No / Abstain" />
          </div>
        </div>
        <button onClick={runVoteSimulate} disabled={vBusy} data-testid="vote-sim-run" className="mt-3 flex items-center gap-2 px-4 py-2 rounded text-sm font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)', opacity: vBusy ? 0.7 : 1 }}>
          <Send size={15} /> {vBusy ? 'Processing…' : 'Simulate reply vote'}
        </button>
        {vResult && (
          <div data-testid="vote-sim-result" className="mt-3 rounded p-3 text-sm" style={{ background: VSTATUS[vResult.status] || '#f3f3f3', border: '1px solid rgba(0,0,0,0.1)' }}>
            <strong>{vResult.status.toUpperCase()}</strong>{vResult.choice ? ` — ${vResult.choice.toUpperCase()}` : ''} — {vResult.reason}
          </div>
        )}
        <h4 className="font-title text-base mt-5 mb-2" style={{ color: 'var(--navy)' }}>Reply-vote log</h4>
        {voteLog.length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--olive)' }} data-testid="vote-log-empty">No reply votes yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="vote-log-table">
              <thead>
                <tr style={{ borderBottom: '2px solid var(--sage)', textAlign: 'left' }}>
                  <th className="py-2 pr-3">When</th>
                  <th className="pr-3">From</th>
                  <th className="pr-3">Choice</th>
                  <th className="pr-3">Status</th>
                  <th className="pr-3">Detail</th>
                </tr>
              </thead>
              <tbody>
                {voteLog.map((v) => (
                  <tr key={v.id} style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                    <td className="py-2 pr-3 whitespace-nowrap">{new Date(v.received_at).toLocaleString()}</td>
                    <td className="pr-3">{v.sender}</td>
                    <td className="pr-3">{v.choice ? <span className="font-title uppercase" style={{ color: v.choice === 'yes' ? '#1c6b2c' : v.choice === 'no' ? '#8a1f11' : '#9a6b00' }}>{v.choice}</span> : '—'}</td>
                    <td className="pr-3"><span className="px-1.5 py-0.5 rounded text-xs" style={{ background: v.status === 'recorded' ? 'var(--sage)' : v.status === 'unclear' ? '#f7e2b0' : '#f2d2cc', color: '#3a2c06' }}>{v.status}</span></td>
                    <td className="pr-3" style={{ color: 'var(--olive)' }}>{v.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm border p-5" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
        <h4 className="font-title text-base mb-3" style={{ color: 'var(--navy)' }}>Forward log</h4>
        {fwdLog.length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--olive)' }}>No forwards yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <div className="mb-2"><TableSearch query={fwdQuery} setQuery={setFwdQuery} placeholder="Search sender, recipient, status…" testid="fwd-search" /></div>
            <table className="w-full text-sm" data-testid="forward-log-table">
              <thead>
                <tr style={{ borderBottom: '2px solid var(--sage)', textAlign: 'left' }}>
                  <SortTh label="When" sortKey="received_at" get={(f) => f.received_at || ''} sort={fwdSort} toggle={toggleFwdSort} className="py-2 pr-3" />
                  <SortTh label="From" sortKey="sender" get={(f) => f.sender || ''} sort={fwdSort} toggle={toggleFwdSort} className="pr-3" />
                  <SortTh label="To" sortKey="to_address" get={(f) => f.to_address || ''} sort={fwdSort} toggle={toggleFwdSort} className="pr-3" />
                  <SortTh label="Status" sortKey="status" get={(f) => f.status || ''} sort={fwdSort} toggle={toggleFwdSort} className="pr-3" />
                  <th className="pr-3">Classes</th>
                  <SortTh label="Recipients" sortKey="recipient_count" get={(f) => f.recipient_count || 0} sort={fwdSort} toggle={toggleFwdSort} className="pr-3" />
                </tr>
              </thead>
              <tbody>
                {sortedFwdLog.map((f) => (
                  <tr key={f.id} style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                    <td className="py-2 pr-3 whitespace-nowrap">{new Date(f.received_at).toLocaleString()}</td>
                    <td className="pr-3">{f.sender}</td>
                    <td className="pr-3">{f.to_address}</td>
                    <td className="pr-3">
                      <span className="px-1.5 py-0.5 rounded text-xs" style={{ background: f.status === 'forwarded' ? 'var(--sage)' : '#f2d2cc', color: '#3a2c06' }}>{f.status}</span>
                    </td>
                    <td className="pr-3">{(f.matched_classes || []).map((c) => classLabel(c)).join(', ') || '—'}</td>
                    <td className="pr-3">{f.recipient_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
